import { useEffect, useRef, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { MessageSquare, X, Send, Loader2, Check, AlertCircle } from "lucide-react"
import { useSession } from "@/hooks/use-session"

const ENDPOINT = "/api/v1/feedback"

type Status = "idle" | "submitting" | "success" | "error" | "rate-limited"

export function FeedbackWidget() {
  const session = useSession()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) setOpen(false)
    }
    window.addEventListener("keydown", onEsc)
    return () => window.removeEventListener("keydown", onEsc)
  }, [open])

  if (session.status !== "authenticated") return null

  const authedEmail = session.user.email

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Send feedback"
        className="
          group fixed bottom-5 right-5 z-40 inline-flex h-10 items-center gap-2
          rounded-full border border-border bg-card px-4 text-sm font-medium
          text-foreground backdrop-blur-md
          transition-[background-color,border-color,color,box-shadow,transform]
          duration-150 ease-out
          shadow-[0_0_0_0_rgba(244,63,94,0)]
          hover:-translate-y-0.5 hover:border-rose-500/40 hover:bg-card
          hover:text-foreground hover:shadow-[0_0_24px_-4px_rgba(244,63,94,0.45)]
          active:translate-y-0
          motion-reduce:hover:translate-y-0
        "
      >
        <span className="relative inline-flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-40 motion-reduce:animate-none" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-rose-400" />
        </span>
        <MessageSquare className="h-3.5 w-3.5 text-muted-foreground transition-colors duration-150 group-hover:text-rose-300" />
        Feedback
      </button>

      <AnimatePresence>
        {open && (
          <FeedbackModal
            initialEmail={authedEmail}
            onClose={() => setOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  )
}

function FeedbackModal({
  initialEmail,
  onClose,
}: {
  initialEmail: string
  onClose: () => void
}) {
  const [email, setEmail] = useState(initialEmail)
  const [message, setMessage] = useState("")
  const [status, setStatus] = useState<Status>("idle")
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const firstFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)

  useEffect(() => {
    const t = setTimeout(() => firstFieldRef.current?.focus(), 60)
    return () => clearTimeout(t)
  }, [])

  const close = () => {
    if (status === "submitting") return
    onClose()
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (status === "submitting") return
    setStatus("submitting")
    setErrorMsg(null)

    try {
      const res = await fetch(ENDPOINT, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          message: message.trim(),
          source: "app",
          page_url: typeof window !== "undefined" ? window.location.href : "",
        }),
      })

      if (res.ok) {
        setStatus("success")
        return
      }
      if (res.status === 429) {
        setStatus("rate-limited")
        return
      }
      const body = (await res.json().catch(() => ({}))) as { error?: string }
      setErrorMsg(body.error || "Something went wrong. Try again in a moment.")
      setStatus("error")
    } catch {
      setErrorMsg("Couldn't reach the server. Try again in a moment.")
      setStatus("error")
    }
  }

  return (
    <>
      <motion.div
        key="backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        onClick={close}
        className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[6px]"
      />
      <motion.div
        key="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-heading"
        initial={{ opacity: 0, scale: 0.97, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.98, y: 4 }}
        transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
        className="fixed left-1/2 top-1/2 z-50 w-[min(94vw,28rem)] -translate-x-1/2 -translate-y-1/2"
      >
        <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-2xl shadow-rose-500/8 backdrop-blur-xl">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-rose-400/40 to-transparent"
          />

          <div className="relative px-6 pb-2 pt-5">
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              className="absolute right-3 top-3 rounded-md p-1.5 text-muted-foreground transition-colors duration-150 hover:bg-foreground/5 hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-rose-400/80">
              <MessageSquare className="h-3 w-3" />
              Feedback
            </div>
            <h2 id="feedback-heading" className="text-lg font-semibold tracking-tight text-foreground">
              {status === "success"
                ? "Thanks for the note."
                : "How can we make Hivehook better?"}
            </h2>
            {status !== "success" && status !== "rate-limited" && (
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                Bug, idea, missing feature, broken docs. We read every one.
              </p>
            )}
          </div>

          {status === "success" ? (
            <div className="px-6 pb-6">
              <div className="flex items-start gap-3 rounded-lg border border-emerald-500/20 bg-emerald-500/6 p-3 text-sm text-emerald-700 dark:text-emerald-200">
                <Check className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Your feedback is on the way. We may reply to {email}.</span>
              </div>
              <button
                type="button"
                onClick={close}
                className="mt-4 w-full rounded-lg bg-foreground/5 px-4 py-2.5 text-sm font-medium text-foreground transition-colors duration-150 hover:bg-foreground/10"
              >
                Done
              </button>
            </div>
          ) : status === "rate-limited" ? (
            <div className="px-6 pb-6">
              <div className="flex items-start gap-3 rounded-lg border border-amber-500/20 bg-amber-500/6 p-3 text-sm text-amber-700 dark:text-amber-200">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>You&apos;ve hit today&apos;s feedback limit (5/day). Try again tomorrow.</span>
              </div>
              <button
                type="button"
                onClick={close}
                className="mt-4 w-full rounded-lg bg-foreground/5 px-4 py-2.5 text-sm font-medium text-foreground transition-colors duration-150 hover:bg-foreground/10"
              >
                Got it
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3 px-6 pb-6 pt-2">
              <label className="block">
                <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                  Your email
                </span>
                <input
                  ref={(el) => {
                    firstFieldRef.current = el
                  }}
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  disabled={status === "submitting"}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 outline-none transition-colors duration-150 focus:border-rose-500/50 focus:ring-2 focus:ring-rose-500/20"
                />
              </label>

              <label className="block">
                <span className="mb-1 flex items-baseline justify-between text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                  <span>Message</span>
                  <span className="text-2xs text-muted-foreground/60 tabular-nums">
                    {message.length}/5000
                  </span>
                </span>
                <textarea
                  required
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, 5000))}
                  rows={4}
                  placeholder="What happened, what would help, or what's missing?"
                  disabled={status === "submitting"}
                  className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 outline-none transition-colors duration-150 focus:border-rose-500/50 focus:ring-2 focus:ring-rose-500/20"
                />
              </label>

              {status === "error" && errorMsg && (
                <div className="flex items-start gap-2.5 rounded-lg border border-rose-500/20 bg-rose-500/6 px-3 py-2 text-xs text-rose-600 dark:text-rose-300">
                  <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" />
                  <span>{errorMsg}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={status === "submitting" || !email || !message}
                className="group inline-flex w-full items-center justify-center gap-2 rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_0_20px_-6px_rgba(244,63,94,0.5)] transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-rose-500 hover:shadow-[0_0_28px_-6px_rgba(244,63,94,0.7)] active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-rose-600/30 disabled:text-rose-100/40 disabled:shadow-none"
              >
                {status === "submitting" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending
                  </>
                ) : (
                  <>
                    Send
                    <Send className="h-3.5 w-3.5 transition-transform duration-150 group-hover:translate-x-0.5" />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </motion.div>
    </>
  )
}
